import React, { useMemo } from "react";
import usePlacesAutocompleteService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import AppSettings from "../../../config";
import { AutocompleteTextBox, AutocompleteTextBoxItem, AutocompleteTextBoxProps } from "./AutocompleteTextBox";

interface PredictionItem {
  id: number;
  prediction: google.maps.places.AutocompletePrediction;
}

interface AutocompleteAddressFieldProps extends AutocompleteTextBoxProps<PredictionItem> {
  options?: google.maps.places.AutocompletionRequest;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  id?: string;
}

/**
 * Accessible address field with autocomplete. Use `onPlaceSelected` to react to
 * the user selecting a recognized place from the autocomplete list.
 */
export const AutocompleteAddressField = ({
  options,
  onInputChange,
  onPlaceSelected,
  id,
  ...props
}: AutocompleteAddressFieldProps) => {
  const { placesService, placePredictions, getPlacePredictions } = usePlacesAutocompleteService({
    apiKey: AppSettings.GOOGLE_GEOCODING_KEY,
    // Turn off debouncing since it causes the menu to sometimes not appear when
    // typing quickly
    debounce: 0,
    options,
  });

  const items = useMemo(
    () => placePredictions.map<PredictionItem>((prediction, index) => ({ id: index, prediction })),
    [placePredictions],
  );

  const onSelectionChange = (index: React.Key) => {
    const placePrediction = placePredictions[index as number];

    if (placePrediction?.place_id) {
      placesService?.getDetails({ placeId: placePrediction.place_id }, (place) => place && onPlaceSelected?.(place));
    }
  };

  return (
    <AutocompleteTextBox
      {...props}
      type='textbox'
      items={items}
      onInputChange={(value) => {
        getPlacePredictions({ input: value });
        onInputChange?.(value);
      }}
      onSelectionChange={onSelectionChange}
      id={id}
    >
      {({ prediction }) => (
        <AutocompleteTextBoxItem key={prediction.id}>
          <span>{prediction.structured_formatting.main_text}</span>
          {prediction.structured_formatting.secondary_text && (
            <span className='tw-block tw-text-sm tw-opacity-75'>{prediction.structured_formatting.secondary_text}</span>
          )}
        </AutocompleteTextBoxItem>
      )}
    </AutocompleteTextBox>
  );
};
